import { defaultPageSizeSelectOptions } from '@liveauctioneers/caterwaul-components/lib/Pagination/components/DefaultPageSizeOptions';
import { SelectOptions } from '@liveauctioneers/caterwaul-components/types/Select';
import { OptionType, Select } from '@liveauctioneers/hammer-ui-core/select';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import React, { ReactNode, useCallback } from 'react';
import isEqual from 'react-fast-compare';

import { ModifyQueryParamsAction, useModifyQueryParams } from '@/hooks/queryParams/useModifyQueryParams';

import { PaginationFilter } from './types/PaginationFilter';

export type SortPageSizePairProps = {
    readonly children?: ReactNode;
    readonly className?: string;
    readonly hideCount?: boolean;
    readonly hideSort?: boolean;
    readonly isInsideLabel?: boolean;
    readonly onChange: Function;
    readonly pageSizeLabel?: ReactNode;
    readonly pageSizeLabelClassName?: string;
    /** SelectOptions of what should be rendered for the page size options */
    readonly pageSizeOptions?: SelectOptions;
    /** A PaginationFilter object */
    readonly paginationFilter: PaginationFilter;
    /** Should modify query params */
    readonly shouldModifyQueryParams?: boolean;
    readonly sortLabel?: ReactNode;
    readonly sortLabelClassName?: string;
    /** SelectOptions of what should be rendered for the sort options */
    readonly sortOptions: SelectOptions;
    readonly testid?: string;
};

export const SortPageSizePair = ({
    children,
    className,
    hideCount = false,
    hideSort = false,
    isInsideLabel = false,
    onChange,
    pageSizeLabel = 'View:',
    pageSizeLabelClassName,
    pageSizeOptions = defaultPageSizeSelectOptions,
    paginationFilter,
    shouldModifyQueryParams,
    sortLabel = 'Sort:',
    sortLabelClassName,
    sortOptions,
    testid = 'sortPageSizePair',
}: SortPageSizePairProps) => {
    const modifyQueryParams = useModifyQueryParams();

    const onSortChange = useCallback(
        (value: OptionType) => {
            const { value: sort } = value ?? {};
            if (shouldModifyQueryParams) {
                modifyQueryParams({
                    action: ModifyQueryParamsAction.ADD,
                    query: { page: 1, sort },
                });
            }
            onChange({ page: 1, sort });
        },
        [shouldModifyQueryParams, onChange, modifyQueryParams]
    );

    const onPageSizeChange = useCallback(
        (value: OptionType) => {
            const { value: pageSize } = value ?? {};
            if (shouldModifyQueryParams) {
                modifyQueryParams({
                    action: ModifyQueryParamsAction.ADD,
                    query: { page: 1, pageSize },
                });
            }
            onChange({ page: 1, pageSize });
        },
        [shouldModifyQueryParams, onChange, modifyQueryParams]
    );

    return (
        <div
            className={cn('flex gap-sm smMax:flex-col', className)}
            data-testid={testid}
        >
            {hideSort ? null : (
                <Select
                    className={cn('min-w-[250px]', sortLabelClassName)}
                    data-testid="sort-select"
                    isInsideLabel={isInsideLabel}
                    labelText={sortLabel}
                    onChange={(option) => onSortChange(option)}
                    options={sortOptions}
                    small
                    usePortalMenu
                    value={sortOptions.find((op) => op.value === paginationFilter.sort)}
                />
            )}
            {hideCount ? null : (
                <Select
                    className={cn('xsMax:min-w-[250px]', pageSizeLabelClassName)}
                    data-testid="page-size-select"
                    isInsideLabel={isInsideLabel}
                    labelText={pageSizeLabel}
                    onChange={(option) => onPageSizeChange(option)}
                    options={pageSizeOptions}
                    small
                    usePortalMenu
                    value={pageSizeOptions.find((op) => op.value === paginationFilter.pageSize)}
                />
            )}
            {children}
        </div>
    );
};

export default React.memo<SortPageSizePairProps>(SortPageSizePair, isEqual);
